import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  FullScreenLoading,
  Icon,
  IconButton,
  Select,
  SuggestionActionInput,
  SvgIcon,
  Text,
} from '@civilgrid/ui/components';
import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { inviteMembers } from '~/api/projectsV3';
import { isValidEmail } from '~/common/miscUtils';
import Notify from '~/components2/Notify';
import rootStore from '~/store';
import { getRoles, isRoleHigherThanMine } from '../../organization/members/common';
import orgMembersStore from '../../organization/members/store';
import { AboutUserRoles, keepOpenOnSuggestionListEscape } from './components';

const EMPTY_ORG_ROLES: readonly unknown[] = [];

interface Props {
  trigger?: ReactNode;
  /** Called after invites are sent successfully (e.g. to refetch the members list). */
  onSave?: () => void | Promise<void>;
}

export default function InviteTeamOrgDialog({ trigger = <Button>Invite Team</Button>, onSave }: Props) {
  const { currentOrganizationDetail, myRole } = useSnapshot(rootStore);
  const { selectedExternalMembers, selectedExternalMembersArray, isLoading } = useSnapshot(orgMembersStore);

  const orgRoles = useMemo(
    () => (currentOrganizationDetail.isLoading ? EMPTY_ORG_ROLES : currentOrganizationDetail.roles),
    [currentOrganizationDetail],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const roles = useMemo(
    () => getRoles().filter((role) => typeof role.value === 'string' && !isRoleHigherThanMine(role.value)),
    [currentOrganizationDetail.isLoading, orgRoles, myRole],
  );

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

  // Comma-separated paste: add each valid email and keep remainder in input
  useEffect(() => {
    if (!searchValue.includes(',')) return;
    const parts = searchValue.split(',');
    let remainder = '';
    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed && isValidEmail(trimmed)) {
        orgMembersStore.addExternalMember(trimmed);
      } else {
        remainder += (remainder ? ',' : '') + part;
      }
    }
    if (remainder !== searchValue) {
      setSearchValue(remainder.trim());
    }
  }, [searchValue]);

  const handleSubmitEmail = (email: string) => {
    if (!isValidEmail(email)) return;
    orgMembersStore.addExternalMember(email);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const isValidState = useMemo<boolean>(() => {
    if (!selectedExternalMembers.size) return false;

    return selectedExternalMembersArray.every(({ email, role }) => {
      if (isValidEmail(email) && role) return true;
      return false;
    });
  }, [selectedExternalMembersArray]);

  const hasAnythingToSubmit = selectedExternalMembersArray.length > 0 && isValidState;

  const handleInviteMembers = async () => {
    if (!hasAnythingToSubmit || currentOrganizationDetail.isLoading) return;
    const orgId = currentOrganizationDetail.id;
    if (!orgId) return;
    orgMembersStore.isLoading = true;

    try {
      await inviteMembers({
        organizationId: orgId,
        memberData: selectedExternalMembersArray.map(({ email, role }) => ({ email, role })),
      });
      orgMembersStore.resetSelections();
      await onSave?.();
      Notify.info({
        message: `Invite${selectedExternalMembers.size > 0 && '(s)'} sent!`,
      });
      setOpen(false);
    } catch (err) {
      console.error(err);
      Notify.error({
        message: `Error sending invite${selectedExternalMembers.size > 0 && '(s)'} !`,
      });
    } finally {
      orgMembersStore.isLoading = false;
    }
  };

  const hasPendingInput = searchValue.trim() !== '' || selectedExternalMembersArray.length > 0;

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (isOpen === false) {
          orgMembersStore.resetSelections();
          setSearchValue('');
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        ref={setContentEl}
        data-testid="invite-team-org-dialog"
        title="Invite Team Members"
        preventDismiss={hasPendingInput}
        onEscapeKeyDown={keepOpenOnSuggestionListEscape}
        // Lets the suggestion list extend past the dialog's bottom edge instead of clipping.
        className="w-150 max-w-auto overflow-visible"
      >
        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Text className="text-xs text-grey-80 font-medium">Add new members to your organization</Text>
            <SuggestionActionInput
              options={[]}
              value={searchValue}
              onChange={setSearchValue}
              onSubmit={handleSubmitEmail}
              customOptionLabel={(v) => (
                <div className="text-xs text-grey-90">
                  {isValidEmail(v) ? (
                    <div className="flex items-center gap-1">
                      <Icon icon="luUserPlus" />
                      <Text className="text-xs text-grey-90">Invite {v} to your organization</Text>
                    </div>
                  ) : (
                    'Please enter a valid email'
                  )}
                </div>
              )}
              container={contentEl}
              placeholder="Enter email address to invite"
            />
          </div>
          <div className="flex flex-col space-y-2">
            <Text className="text-xs">
              Enter an email address to invite someone to your organization. You can add multiple people.
            </Text>
          </div>
          {selectedExternalMembers.size > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {/* Header row */}
              <div className="col-span-1 flex items-end">
                <Text className="text-sm font-medium leading-4">Invite to CivilGrid</Text>
              </div>
              <div className="col-span-1 flex items-end gap-1">
                <AboutUserRoles
                  trigger={
                    <div className="flex items-center gap-1 cursor-pointer tab-index-0">
                      <Text className="text-xs font-medium leading-4">About Roles</Text>
                      <Icon icon="questionMarkCircled" />
                    </div>
                  }
                />
              </div>
              <div className="col-span-1" />
              {/* Data rows: email | role select | apply to all | trash */}
              {selectedExternalMembersArray.map((member) => (
                <React.Fragment key={member.email}>
                  <div className="col-span-1 flex items-center min-w-0">
                    <Text className="text-xs text-grey-80 truncate">{member.email}</Text>
                  </div>
                  <div className="col-span-1 flex flex-col gap-0.5 min-w-0">
                    <Select
                      options={roles}
                      value={member.role ?? ''}
                      onValueChange={(newRole) => newRole && orgMembersStore.updateMemberRole(member.email, newRole)}
                      placeholder="Select role..."
                      aria-label={`Role for ${member.email}`}
                      aria-invalid={!member.role}
                      triggerClassName="w-40"
                      data-testid={`invite-team-org-role-select-${member.email}`}
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-between">
                    {member.role ? (
                      selectedExternalMembersArray.length > 1 ? (
                        <IconButton
                          icon="copy"
                          size="icon"
                          variant="hollow"
                          tooltip="Apply role to all invited members"
                          onClick={() => member.role && orgMembersStore.updateAllMembersRole(member.role)}
                        >
                          <Text className="text-xs text-grey-80">Apply to all</Text>
                        </IconButton>
                      ) : (
                        <div className="w-4" />
                      )
                    ) : (
                      <Text className="text-xs text-destructive">A role is required</Text>
                    )}
                    <Button
                      size="icon"
                      variant="hollow"
                      type="button"
                      aria-label={`Remove ${member.email}`}
                      onClick={() => orgMembersStore.toggleExternalMember(member.email)}
                      data-testid={`invite-team-org-remove-${member.email}`}
                    >
                      <SvgIcon icon="trash" size={14} />
                    </Button>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleInviteMembers} disabled={!hasAnythingToSubmit}>
            Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
